import { useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import cn from 'classnames';
import { VStack } from '@direct-frontend/components';
import { useFreezeHeight } from './lib/hooks/useFreezeHeight';
import { ContainerTreeSizeProvider } from '../ContainerTreeSizeProvider';
import { TreeEditorContent } from './TreeEditorContent';
import { type TreeEditorProps } from './types';
import { useTreeEditorRows } from './useTreeEditorRows';
import styles from './TreeEditor.module.css';

export function ElementTreeEditor<Attributes>(props: TreeEditorProps<Attributes>) {
    const {
        'data-testid': dataTestId = 'TreeEditor',
        isVirtualized = false,
        disableDragAndDrop = false,
        settings,
    } = props;
    const flatNodes = useTreeEditorRows(props);
    const visibleNodes = useMemo(
        () => (props.isNodeVisible ? flatNodes.filter(props.isNodeVisible) : flatNodes),
        [flatNodes, props.isNodeVisible]
    );
    const { ref, setFreeze, setUnfreeze } = useFreezeHeight();
    // useVirtualizer возвращает мутабельный инстанс, мемоизация которого может привести к устаревшему UI.
    // eslint-disable-next-line react-hooks/incompatible-library -- React Compiler пропускает мутабельный Virtualizer
    const virtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
        getScrollElement: () => ref.current,
        estimateSize: () => 44,
        getItemKey: (index) => visibleNodes[index]?.id ?? index,
        count: visibleNodes.length,
        overscan: 50,
    });
    const virtualItems = virtualizer.getVirtualItems();
    const items = useMemo(() => {
        if (!isVirtualized) {
            return visibleNodes;
        }

        return virtualItems.map((virtualItem) => visibleNodes[virtualItem.index]);
    }, [isVirtualized, visibleNodes, virtualItems]);
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
    const paddingBottom =
        virtualItems.length > 0 ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end : 0;

    return (
        <VStack
            ref={ref}
            className={cn(styles.container, settings?.editorStyles)}
            scrollable
            data-testid={dataTestId}
            data-dnd-viewport={!disableDragAndDrop}
            minWidth="100%"
            minHeight={isVirtualized ? '1px' : undefined}
            height="max"
            maxHeight={settings?.maxHeight ?? '100%'}
        >
            <div style={{ width: '100%' }}>
                <ContainerTreeSizeProvider containerRef={ref}>
                    <TreeEditorContent
                        {...props}
                        flatNodes={flatNodes}
                        items={items}
                        paddingTop={paddingTop}
                        paddingBottom={paddingBottom}
                        useVirtualSpacers={isVirtualized}
                        onDragStartEffect={() => {
                            if (!settings?.withoutHeightFreeze) {
                                setFreeze();
                            }
                        }}
                        onDragEndEffect={setUnfreeze}
                    />
                </ContainerTreeSizeProvider>
            </div>
        </VStack>
    );
}
